import React, { useEffect, useState, useCallback } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { Home as HomeIcon, Users, PlusCircle, Megaphone, User, LogOut, MessageSquare } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { UserAvatar } from "@/components/UserAvatar";
import { Button } from "@/components/ui/button";
import { fullName } from "@/lib/format";
import api from "@/lib/api";

const NAV = [
  { to: "/", label: "Home", icon: HomeIcon, testid: "nav-home" },
  { to: "/directory", label: "Directory", icon: Users, testid: "nav-directory" },
  { to: "/post", label: "Post", icon: PlusCircle, testid: "nav-post" },
  { to: "/messages", label: "Messages", icon: MessageSquare, testid: "nav-messages", badge: true },
  { to: "/announcements", label: "Announcements", icon: Megaphone, testid: "nav-announcements" },
  { to: "/profile", label: "Profile", icon: User, testid: "nav-profile" },
];

function NavIcon({ Icon, unread }) {
  return (
    <div className="relative">
      <Icon className="w-5 h-5" />
      {unread > 0 && (
        <span
          className="absolute -top-1.5 -right-2 min-w-[16px] h-4 px-1 rounded-full bg-[#B31B1B] text-white text-[9px] font-bold grid place-items-center leading-none"
          data-testid="nav-unread-badge"
        >
          {unread > 99 ? "99+" : unread}
        </span>
      )}
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);

  const loadUnread = useCallback(async () => {
    try {
      const { data } = await api.get("/messages/unread-count");
      setUnread(data.count || 0);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    loadUnread();
    const t = setInterval(loadUnread, 10000);
    return () => clearInterval(t);
  }, [loadUnread]);

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Top header (mobile) */}
      <header className="lg:hidden sticky top-0 z-30 bg-white/85 backdrop-blur-md border-b border-border">
        <div className="px-4 h-14 flex items-center justify-between safe-top">
          <NavLink to="/" className="flex items-center gap-2" data-testid="brand-header-mobile">
            <div className="w-8 h-8 rounded-lg bg-[#B31B1B] text-white grid place-items-center font-bold" style={{ fontFamily: "Georgia, serif" }}>C</div>
            <div className="leading-tight">
              <div className="font-extrabold text-[15px] tracking-tight">CCNEO Connect</div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-wider">Northeast Ohio</div>
            </div>
          </NavLink>
          <NavLink to="/profile" data-testid="header-avatar-link">
            <UserAvatar user={user || {}} size={34} />
          </NavLink>
        </div>
      </header>

      <div className="lg:flex lg:max-w-6xl lg:mx-auto">
        {/* Desktop sidebar */}
        <aside className="hidden lg:flex lg:flex-col lg:w-64 lg:sticky lg:top-0 lg:h-screen lg:py-6 lg:px-4 border-r border-border bg-white/60">
          <NavLink to="/" className="flex items-center gap-3 px-2 mb-8" data-testid="brand-header-desktop">
            <div className="w-10 h-10 rounded-xl bg-[#B31B1B] text-white grid place-items-center text-xl font-bold" style={{ fontFamily: "Georgia, serif" }}>C</div>
            <div>
              <div className="font-extrabold text-lg tracking-tight">CCNEO Connect</div>
              <div className="text-[11px] text-muted-foreground uppercase tracking-wider">Cornell Club NEO</div>
            </div>
          </NavLink>
          <nav className="flex-1 space-y-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`${item.testid}-desktop`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive ? "bg-[#B31B1B] text-white" : "text-neutral-700 hover:bg-neutral-100"
                  }`
                }
              >
                <NavIcon Icon={item.icon} unread={item.badge ? unread : 0} />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>
          <div className="pt-4 border-t border-border mt-4">
            <NavLink to="/profile" className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-neutral-100" data-testid="sidebar-user-link">
              <UserAvatar user={user || {}} size={40} />
              <div className="min-w-0">
                <div className="font-semibold text-sm truncate">{fullName(user)}</div>
                <div className="text-xs text-muted-foreground truncate">{user?.member_type}</div>
              </div>
            </NavLink>
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start gap-2 mt-2 text-neutral-600"
              onClick={handleLogout}
              data-testid="logout-button-desktop"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </Button>
            <p className="mt-4 text-[10px] text-center text-muted-foreground">Powered by SocialImpactCLE</p>
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 min-w-0 pb-28 lg:pb-8">
          <div className="max-w-2xl mx-auto px-4 lg:px-8 py-4 lg:py-8">
            <Outlet context={{ refreshUnread: loadUnread }} />
          </div>
          <footer className="lg:hidden text-center text-[10px] text-muted-foreground pb-4 pt-2">
            Powered by SocialImpactCLE
          </footer>
        </main>
      </div>

      {/* Bottom nav (mobile) */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-white/90 backdrop-blur-md border-t border-border safe-bottom">
        <div className="grid grid-cols-6">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={`${item.testid}-mobile`}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center py-2 gap-0.5 text-[9px] font-medium ${
                  isActive ? "text-[#B31B1B]" : "text-neutral-500"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {item.to === "/post" ? (
                    <div className={`w-10 h-10 rounded-full grid place-items-center -mt-2 bg-[#B31B1B] text-white shadow-md`}>
                      <item.icon className="w-5 h-5" />
                    </div>
                  ) : (
                    <NavIcon Icon={item.icon} unread={item.badge ? unread : 0} />
                  )}
                  <span className="truncate max-w-full px-1">{item.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
